import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { DepositBloc } from '@connect-services/client/blocs/deposit.bloc';
import { DepositFolderListBloc } from '@connect-services/client/blocs/deposit-folder-list.bloc';
import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { AbstractPaginationComponent } from '@connect-services/client/components/abstract-pagination/abstract-pagination.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import { CreateEditDataModalForm } from '@connect-services/client/components/modals/create-edit-data-modal/create-edit-data-modal.component';
import { AddDepositModalForm } from '@connect-services/client/components/modals/deposit/add-deposit-modal/add-deposit-modal.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { RouteCompletePathEnum, RoutePathEnum } from '@connect-services/client/models/routes.model';
import { DataViewType } from '@connect-services/client/pages/management/data/deposit/deposit.component';
import { DetailPanelService } from '@connect-services/client/services/detail-panel.service';
import { DocumentService } from '@connect-services/client/services/document.service';
import { SensitiveSortColumns } from '@connect-services/client/services/table.service';
import { TranslateService } from '@connect-services/client/services/translate.service';
import { WarehouseAndFormsAccessPermissionsService } from '@connect-services/client/services/warehouse-and-forms-access-permissions.service';
import { PaginatorComponent } from '@connect-services/src/lib/components/paginator/paginator.component';
import { CustomColumn, RecordDataKey, SortState } from '@connect-services/src/lib/components/table/table.component';
import {
    Deposit,
    DepositControllerGet200Response,
    DepositFolder,
    DepositFoldersService,
    DepositsService,
} from '@connect-services/src/sdk/api';
import DepositStatusEnum from '@connect-services/src/sdk/async/DepositStatusEnum';
import { OrderEnum, PaginationParameters } from '@shared/client/models/pagination.model';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export interface DepositFolderTableRow extends RecordDataKey {
    createdAt?: Date;
    deleteData?: BehaviourButton;
    deposit?: Deposit;
    id: string;
    name: string;
    standard: string;
    status?: Deposit.StatusEnum;
    tags: string;
    version: string | number;
}

export interface DepositTableRow extends RecordDataKey {
    createdAt?: Date;
    hadDataAtImport?: boolean;
    id: string;
    isValid?: boolean;
    name?: string;
    status?: Deposit.StatusEnum;
    version?: number;
}

export interface DeleteBehaviourInput {
    deposit: Deposit | undefined;
    isDeletable: {
        hasLostDocuments: boolean;
        isDepositUsedInLayer: boolean;
    };
}

export class BehaviourButton {
    public disabledMessage: string[] | undefined;
    public isActive: boolean;

    public constructor(isActive: boolean, disabledMessage: string[] | undefined) {
        this.disabledMessage = disabledMessage;
        this.isActive = isActive;
    }
}

export interface DepositImportToAbort {
    id: string;
    status?: Deposit.StatusEnum;
}

const REFERENCE_DATA_COLUMNS: CustomColumn[] = [
    { key: 'name', label: 'dataName' },
    { key: 'version', label: 'lastVersion' },
    { key: 'createdAt', label: 'lastImportDate' },
    { key: 'standard', label: 'standard' },
];

const ORGANIZATION_DATA_COLUMNS: CustomColumn[] = [
    { key: 'name', label: 'dataName' },
    { key: 'version', label: 'lastVersion' },
    { key: 'status', label: '', sortable: false },
    { key: 'createdAt', label: 'lastImportDate' },
    { key: 'standard', label: 'standard' },
    { key: 'deleteData', label: '', sortable: false },
    { key: 'updateData', label: '', sortable: false },
    { key: 'tags', label: '', sortable: false },
];

@Injectable({
    providedIn: 'root',
})
export class ManagementDataService extends AbstractPaginationComponent {
    public readonly abortingDepositIds = signal<ReadonlySet<string>>(new Set());
    public readonly canManageDeposit = computed(() =>
        this.warehouseAndFormsAccessPermissionsService.canManageDeposit(),
    );
    public readonly currentView: WritableSignal<DataViewType> = signal(DataViewType.ORGANIZATION_DATA);
    public readonly customColumns = computed<CustomColumn[]>(() => {
        if (this.currentView() === DataViewType.REFERENCE_DATA) {
            return REFERENCE_DATA_COLUMNS;
        }

        return ORGANIZATION_DATA_COLUMNS;
    });
    public readonly dataColumn: WritableSignal<DepositFolderTableRow[]> = signal([]);
    public readonly expandedElementId: WritableSignal<string | null> = signal<string | null>(null);
    public readonly isEmptyWarehouse: WritableSignal<boolean> = signal(false);
    public readonly lastDepositImported: WritableSignal<Deposit | undefined> = signal<Deposit | undefined>(undefined);
    public readonly pagination: WritableSignal<PaginationParameters> = signal<PaginationParameters>({
        isCaseSensitive: true,
        order: OrderEnum.ASC,
        orderBy: 'name',
        page: 0,
        take: 10,
    });
    public paginator?: PaginatorComponent;
    public readonly searchValue: WritableSignal<string> = signal<string>('');

    protected readonly depositFolderListBloc = inject(DepositFolderListBloc);
    protected readonly documentService = inject(DocumentService);
    protected readonly translateService = inject(TranslateService);

    private depositBloc = inject(DepositBloc);
    private readonly depositFoldersService = inject(DepositFoldersService);
    private readonly depositsService = inject(DepositsService);
    private readonly detailPanelService = inject(DetailPanelService);
    private readonly matDialog = inject(MatDialog);
    private readonly router = inject(Router);
    private readonly snackBarService = inject(SnackBarService);
    private readonly translocoService = inject(TranslocoService);
    private readonly warehouseAndFormsAccessPermissionsService = inject(WarehouseAndFormsAccessPermissionsService);

    public constructor() {
        super();

        this.documentService.documentListChanges.subscribe(async () => {
            await this.refreshDeposit();
            await this.refreshDepositFolderList();
        });
    }

    public async abortDepositImport(deposit: DepositImportToAbort): Promise<void> {
        if (deposit.status !== Deposit.StatusEnum.IMPORTING || this.abortingDepositIds().has(deposit.id)) {
            return;
        }

        this.abortingDepositIds.update((ids: ReadonlySet<string>) => new Set(ids).add(deposit.id));

        try {
            await firstValueFrom(
                this.depositsService.depositControllerAbortImport({
                    id: deposit.id,
                }),
            );

            await this.refreshDepositFolderList();

            if (this.depositBloc.value()?.id === deposit.id) {
                await this.router.navigate([...RouteCompletePathEnum.ORGANIZATION_DATA]);
            }

            this.snackBarService.openSuccessSnackBar('depositImportAbortSuccess');
        } catch {
            this.snackBarService.openErrorSnackBar('depositImportAbortError');
        } finally {
            this.abortingDepositIds.update((ids: ReadonlySet<string>) => {
                const nextIds = new Set(ids);
                nextIds.delete(deposit.id);

                return nextIds;
            });
        }
    }

    public checkIsValidDeposit(
        depositTableRow: DepositTableRow,
        depositList: DepositControllerGet200Response,
    ): boolean {
        const deposit = depositList.items.find((deposit: Deposit) => deposit.id === depositTableRow.id)!;

        if (!deposit?.depositValidation) {
            return true;
        }

        if (deposit.depositValidation.isValid === undefined) {
            return !!deposit.depositValidation.errors;
        }

        return deposit.depositValidation.isValid;
    }

    public async createDeposit(depositForm: AddDepositModalForm): Promise<void> {
        try {
            this.lastDepositImported.set(
                await firstValueFrom(
                    this.depositsService.depositControllerPost({
                        depositFolder: depositForm.controls.depositFolder.getRawValue()!,
                        file: depositForm.controls.file.getRawValue()!,
                        name: depositForm.controls.name.getRawValue(),
                        standard: depositForm.controls.standard.getRawValue() ?? undefined,
                    }),
                ),
            );

            await this.refreshDepositFolderList();

            this.snackBarService.openSuccessSnackBar('depositSentSuccess');

            if (!this.depositBloc.value()) {
                return;
            }

            await this.refreshDetailPanelIfNeeded(this.depositBloc.value(), this.lastDepositImported());
        } catch {
            this.snackBarService.openErrorSnackBar('errorPOST');
        }
    }

    public async createDepositFolder(depositFolderForm: CreateEditDataModalForm): Promise<void> {
        try {
            await firstValueFrom(
                this.depositFoldersService.depositFolderControllerPost({
                    createDepositFolderDto: {
                        name: depositFolderForm!.value!.name!,
                        standard: depositFolderForm.value.standard ?? undefined,
                    },
                }),
            );

            this.searchValue.set(depositFolderForm.value.name!);

            this.snackBarService.openSuccessSnackBar('successDataCreated');

            await this.refreshDepositFolderList();
        } catch {
            this.snackBarService.openErrorSnackBar('errorPOST');
        }
    }

    public displayTable(): void {
        this.createCustomTable();

        this.setPaginationParameters();
    }

    public findDepositFolder(deposit: DepositFolderTableRow): DepositFolder {
        return this.depositFolderListBloc
            .value()!
            .items.find((depositFolder: DepositFolder) => depositFolder.id === deposit.id)!;
    }

    public findLastDeposit(depositFolderTableRow: DepositFolderTableRow): Deposit | undefined {
        const depositFolder: DepositFolder | undefined = this.depositFolderListBloc
            .value()!
            .items.find((depositFolder: DepositFolder) => depositFolder.id === depositFolderTableRow.id);

        return depositFolder?.deposit ?? undefined;
    }

    public async onPaginationChange(page: number): Promise<void> {
        const paginationParameters = {
            ...this.pagination(),
            page: page,
        };

        this.pagination.set(paginationParameters);

        await this.depositFolderListBloc.load({
            ...paginationParameters,
            type: this.currentView(),
            value: this.searchValue(),
        });
    }

    public onSearchDepositFolder(value: string): void {
        if (!this.paginator) {
            return;
        }

        this.paginator.resetPaginator();

        this.searchValue.set(value);

        this.depositFolderListBloc.load({
            ...this.pagination(),
            type: this.currentView(),
            value: value,
        });
    }

    public async onSortChange(sort?: SortState): Promise<void> {
        if (!this.paginator) {
            return;
        }

        this.paginator.resetPaginator();

        if (!sort) {
            this.pagination.set({
                ...this.pagination(),
                orderBy: undefined,
            });

            await this.depositFolderListBloc.load({
                ...this.pagination(),
                type: this.currentView(),
            });

            return;
        }

        const isSensitiveColumn = Object.values(SensitiveSortColumns).includes(sort.orderBy as SensitiveSortColumns);

        this.pagination.set({
            ...this.pagination(),
            isCaseSensitive: isSensitiveColumn,
            order: sort.order!,
            orderBy: sort.orderBy,
            page: this.pagination().page,
        });

        await this.depositFolderListBloc.load({
            ...this.pagination(),
            type: this.currentView(),
        });

        this.displayTable();
    }

    public openAbortDepositImportConfirmation(deposit: DepositImportToAbort): void {
        if (deposit.status !== Deposit.StatusEnum.IMPORTING || this.abortingDepositIds().has(deposit.id)) {
            return;
        }

        const dialogRef = this.matDialog.open(ConfirmationModal, {
            data: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmLabel: this.translocoService.translate('stopImport'),
                description: this.translocoService.translate('confirmStopImportDescription'),
                title: this.translocoService.translate('confirmStopImportTitle'),
                type: ModalType.DANGER,
            },
            width: '25rem',
        });

        dialogRef.afterClosed().subscribe(async (result: ModalResultEnum): Promise<void> => {
            if (result !== ModalResultEnum.CONFIRM) {
                return;
            }

            await this.abortDepositImport(deposit);
        });
    }

    public async openDepositDetailPanel(depositId: string): Promise<void> {
        const depositDetailId = this.detailPanelService.getDetailPanelUuidFromUrl();
        const baseRoute = this.getManagementDataRoute(this.currentView());

        if (depositDetailId === depositId) {
            this.detailPanelService.clear();

            await this.router.navigate(baseRoute);

            return;
        }

        await this.router.navigate([
            ...baseRoute,
            {
                outlets: {
                    detailPanel: depositId,
                },
            },
        ]);
    }

    public async openDepositEdition(id: string): Promise<void> {
        if (!this.canManageDeposit()) {
            return;
        }

        await this.router.navigate([
            ...RouteCompletePathEnum.ORGANIZATION_DATA,
            { outlets: { detailPanel: [id, RoutePathEnum.DEPOSIT_EDITION] } },
        ]);
    }

    public refreshDeposit = async (): Promise<void> => {
        const deposit = this.depositBloc.value();
        if (!deposit) {
            return;
        }

        await this.depositBloc.load({ id: deposit.id }, { refresh: true });
    };

    public refreshDepositFolderList = async (): Promise<void> => {
        await this.depositFolderListBloc.load(
            {
                ...this.pagination(),
                ...(this.searchValue() && { value: this.searchValue() }),
            },
            {
                refresh: true,
            },
        );
    };

    public async refreshDetailPanelIfNeeded(
        depositBloc: Deposit | null,
        lastDepositImported: Deposit | undefined,
    ): Promise<void> {
        if (!depositBloc || !lastDepositImported) {
            return;
        }
        const folderIdOfCurrentDeposit = depositBloc.depositFolder.toString();

        const lastDepositImportedDepositFolder = lastDepositImported.depositFolder as unknown as DepositFolder;

        if (folderIdOfCurrentDeposit !== lastDepositImportedDepositFolder.id) {
            return;
        }

        if (!this.detailPanelService.isOpen()) {
            return;
        }

        this.detailPanelService.clear();

        await this.router.navigate([
            ...RouteCompletePathEnum.ORGANIZATION_DATA,
            { outlets: { detailPanel: lastDepositImported!.id } },
        ]);
    }

    public setDeleteActionState(context: DeleteBehaviourInput): BehaviourButton {
        if (!this.canManageDeposit()) {
            return new BehaviourButton(false, [this.translocoService.translate('noPermissionToDeleteDeposit')]);
        }

        const cantDeleteReasons: string[] = [];

        if (context.isDeletable.hasLostDocuments) {
            const canManageDocuments = this.documentService.canManageDocuments();

            cantDeleteReasons.push(
                !canManageDocuments
                    ? this.translocoService.translate('noPermissionToReadDocumentsThereForeCannotDeleteDeposit')
                    : this.translocoService.translate('lostDocumentsAssociatedWithDeposit'),
            );
        }

        if (context.isDeletable.isDepositUsedInLayer) {
            cantDeleteReasons.push(this.translocoService.translate('depositUsedByLayers'));
        }

        const isLockedStatus =
            context.deposit?.status === DepositStatusEnum.EXTRACTING ||
            context.deposit?.status === DepositStatusEnum.IMPORTING ||
            context.deposit?.status === DepositStatusEnum.VALIDATING;
        if (isLockedStatus) {
            cantDeleteReasons.push(this.translocoService.translate('depositValidateInProgress'));
        }

        return new BehaviourButton(!cantDeleteReasons.length, cantDeleteReasons.length ? cantDeleteReasons : undefined);
    }

    public toggleExpandedElement(depositFolderId: string): void {
        this.expandedElementId.set(this.expandedElementId() === depositFolderId ? null : depositFolderId);
    }

    public async updateDepositFolder(
        depositFolderForm: CreateEditDataModalForm,
        dataFolder: DepositFolder,
    ): Promise<void> {
        try {
            await firstValueFrom(
                this.depositFoldersService.depositFolderControllerPatch({
                    id: dataFolder.id,
                    updateDepositFolderDto: {
                        name: depositFolderForm!.value!.name!,
                    },
                }),
            );

            this.snackBarService.openSuccessSnackBar('successDataUpdated');

            await this.refreshDepositFolderList();
        } catch {
            this.snackBarService.openErrorSnackBar('errorPATCH');
        }
    }

    private createCustomTable(): void {
        this.dataColumn.set(
            this.depositFolderListBloc
                .value()!
                .items.map((depositFolder: DepositFolder): DepositFolderTableRow | undefined => {
                    const deposit: Deposit | undefined = depositFolder.deposit;

                    return {
                        createdAt: deposit?.createdAt,
                        deleteData: this.setDeleteActionState({
                            deposit: depositFolder.deposit,
                            isDeletable: depositFolder.isDeletable ?? {
                                hasLostDocuments: false,
                                isDepositUsedInLayer: false,
                            },
                        }),
                        id: depositFolder.id,
                        name: depositFolder.name,
                        standard: depositFolder.standard?.name ?? '-',
                        status: deposit?.status,
                        tags: '',
                        version: deposit?.version ?? '-',
                    };
                })
                .filter((depositFolder: DepositFolderTableRow | undefined): depositFolder is DepositFolderTableRow => {
                    return depositFolder !== undefined;
                }),
        );
    }

    private currentViewToPathEnum(currentView: DataViewType): RoutePathEnum {
        if (currentView === DataViewType.REFERENCE_DATA) {
            return RoutePathEnum.REFERENCE_DATA;
        }

        return RoutePathEnum.ORGANIZATION_DATA;
    }

    private getManagementDataRoute(currentView: DataViewType): string[] {
        return [RoutePathEnum.MANAGEMENT_DATA, this.currentViewToPathEnum(currentView)];
    }

    private setPaginationParameters(): void {
        this.pagination.update((paginationParameters) => {
            return {
                ...paginationParameters,
                totalItems: this.depositFolderListBloc.value()!.totalItems,
            };
        });
    }
}
